import Keycloak from "keycloak-js";
import Cookies from "js-cookie";
import axios from "axios";

// ✅ Singleton Keycloak Instance
let keycloakInstance = null;
// NOTE: keycloakInitialized is NOT used anymore; kept here only if you refer to it somewhere else.
// You can remove it later once we're stable.
let keycloakInitialized = false;
//ACB 11/29 Added
// --- Fallback: capture Keycloak /token responses and store access_token ---
(function attachKeycloakTokenSniffer() {
  try {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      // Flag XHRs going to Keycloak's token endpoint
      this._isKeycloakTokenRequest =
        typeof url === "string" &&
        url.includes("/realms/") &&
        url.includes("/protocol/openid-connect/token");
      this._kcUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (this._isKeycloakTokenRequest) {
        this.addEventListener("load", function () {
          try {
            // Only consider successful responses
            if (this.status >= 200 && this.status < 300 && this.responseText) {
              const json = JSON.parse(this.responseText);
              if (json && json.access_token) {
                console.log("🟢 [KC Fallback] Captured access_token from XHR. Length:", json.access_token.length);
                // Store token in all the usual places
                localStorage.setItem("access_token", json.access_token);
                sessionStorage.setItem("access_token", json.access_token);
                try {
                  Cookies.set("access_token", json.access_token, { path: "/" });
                } catch (e) {
                  console.warn("⚠️ [KC Fallback] Failed to set cookie:", e);
                }
              } else {
                console.warn("⚠️ [KC Fallback] /token response had no access_token field.");
              }
            } else {
              console.warn(
                "⚠️ [KC Fallback] Token request failed or had no body:",
                this.status,
                this.responseText
              );
            }
          } catch (e) {
            console.error("❌ [KC Fallback] Error parsing /token response:", e);
          }
        });
      }

      return originalSend.apply(this, args);
    };

    console.log("🔧 [KC Fallback] Token sniffer attached to XMLHttpRequest.");
  } catch (e) {
    console.warn("⚠️ [KC Fallback] Failed to attach token sniffer:", e);
  }
})();

// ---------- Realm helpers ----------

// 🔹 NEW: read realm from URL if present
const getRealmFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);

    // accept realm / Realm / REALM
    const urlRealm =
      params.get("realm") ||
      params.get("Realm") ||
      params.get("REALM");

    if (urlRealm && urlRealm.trim()) {
      console.log("🔍 [KC] Found realm in URL:", urlRealm);
      return urlRealm.trim();
    } else {
      console.log("🔍 [KC] No realm query param in URL");
    }
  } catch (e) {
    console.warn("⚠️ [KC] Could not read realm from URL:", e);
  }
  return null;
};


const REALM_KEY = "active_realm";
const DEFAULT_REALM = "Idenza";

export const getCurrentRealm = () => {
  console.log("🔍 [KC] getCurrentRealm() called");

  // 1️⃣ If realm is in the URL, trust that and sync to sessionStorage
  const urlRealm = getRealmFromUrl();
  if (urlRealm) {
    const stored = sessionStorage.getItem(REALM_KEY);
    if (stored !== urlRealm) {
      console.log("🔹 [KC] Realm from URL differs from stored. Updating sessionStorage:", {
        stored,
        urlRealm,
      });
      sessionStorage.setItem(REALM_KEY, urlRealm);
    } else {
      console.log("🔹 [KC] Realm from URL matches stored realm:", stored);
    }
    return urlRealm;
  }

  // 2️⃣ Else, use the stored realm if we have one
  const storedRealm = sessionStorage.getItem(REALM_KEY);
  if (storedRealm) {
    console.log("🔹 [KC] Using stored realm from sessionStorage:", storedRealm);
    return storedRealm;
  }

  // 3️⃣ Final fallback: default realm (and persist it)
  console.warn("⚠️ [KC] No realm in URL or sessionStorage, using DEFAULT_REALM:", DEFAULT_REALM);
  //sessionStorage.setItem(REALM_KEY, DEFAULT_REALM);
  return DEFAULT_REALM;
};

// ACB 11/28 Modified
export const setCurrentRealm = (realm) => {
  console.log("🔹 [KC] setCurrentRealm() called. Setting current realm to:", realm);
  sessionStorage.setItem(REALM_KEY, realm);
  // Force new instance next time so it picks up the new realm
  keycloakInstance = null;
  // Resetting this is technically unnecessary, but logged for clarity
  keycloakInitialized = false;
};

// ---------- Keycloak instance & init ----------

export const getKeycloakInstance = () => {
  if (!keycloakInstance) {
    const realm = getCurrentRealm();
    console.log("🔧 [KC] Creating new Keycloak instance with config:", {
      url: "https://keycloak.idenza.ai",
      realm,
      clientId: "multi-tenant-client",
    });

    keycloakInstance = new Keycloak({
      url: "https://keycloak.idenza.ai",
      realm,
      clientId: "multi-tenant-client",
    });

    console.log("🔹 [KC] Created Keycloak instance for realm:", realm);

    // Attach event handlers for extra debug
    keycloakInstance.onReady = (authenticated) => {
      console.log("📌 [KC] onReady fired. Authenticated:", authenticated);
    };

    // 🔑 UPDATED: persist token on auth success
    keycloakInstance.onAuthSuccess = () => {
      console.log("✅ [KC] onAuthSuccess fired");
      try {
        if (keycloakInstance.token) {
          console.log("🔹 [KC] onAuthSuccess: storing token");
          console.log("🔹 [KC] onAuthSuccess: token length:", keycloakInstance.token.length);
          console.log("🔹 [KC] onAuthSuccess: tokenParsed:", keycloakInstance.tokenParsed);
          localStorage.setItem("access_token", keycloakInstance.token);
          sessionStorage.setItem("access_token", keycloakInstance.token);
          Cookies.set("access_token", keycloakInstance.token, { path: "/" });
          try {
            getTenantFromToken();
          } catch (e) {
            console.warn("⚠️ [KC] getTenantFromToken() failed in onAuthSuccess:", e);
          }
        } else {
          console.warn("⚠️ [KC] onAuthSuccess fired but no token present");
        }
      } catch (e) {
        console.error("❌ [KC] Error in onAuthSuccess handler:", e);
      }
    };

    keycloakInstance.onAuthError = (errorData) => {
      console.error("❌ [KC] onAuthError fired:", errorData);
    };

    // 🔁 UPDATED: also persist on refresh
    keycloakInstance.onAuthRefreshSuccess = () => {
      console.log("🔄 [KC] onAuthRefreshSuccess fired");
      try {
        if (keycloakInstance.token) {
          console.log("🔹 [KC] onAuthRefreshSuccess: storing refreshed token");
          localStorage.setItem("access_token", keycloakInstance.token);
          sessionStorage.setItem("access_token", keycloakInstance.token);
          Cookies.set("access_token", keycloakInstance.token, { path: "/" });
        }
      } catch (e) {
        console.error("❌ [KC] Error in onAuthRefreshSuccess handler:", e);
      }
    };

    keycloakInstance.onAuthRefreshError = () => {
      console.error("❌ [KC] onAuthRefreshError fired");
    };
    keycloakInstance.onTokenExpired = () => {
      console.warn("⚠️ [KC] onTokenExpired fired");
    };
  } else {
    console.log("🔁 [KC] Reusing existing Keycloak instance with realm:", keycloakInstance.realm);
  }
  return keycloakInstance;
};

// ✅ Initialize Keycloak with check-sso (safe to call multiple times)
export const initKeycloak = async () => {
  const keycloak = getKeycloakInstance();

  console.log("🔧 [KC] initKeycloak() called. Config about to use:", {
    url: "https://keycloak.idenza.ai",
    realm: keycloak.realm,
    clientId: keycloak.clientId,
  });

  let authenticated = false;

  try {
    console.log("🔹 [KC] Calling keycloak.init({ onLoad: 'check-sso', ... })...");
    authenticated = await keycloak.init({
      onLoad: "check-sso",
      checkLoginIframe: false,
      pkceMethod: "S256",
      responseMode: "query",
    });
    console.log("🔹 [KC] keycloak.init() resolved. Authenticated:", authenticated);
    
    console.log("🔹 [KC] keycloak.authenticated:", keycloak.authenticated);
    console.log("🔹 [KC] keycloak.token present:", !!keycloak.token);

    if (authenticated && keycloak.token) {
      console.log("✅ [KC] Authenticated in initKeycloak. Token present:", !!keycloak.token);
      localStorage.setItem("access_token", keycloak.token);
      sessionStorage.setItem("access_token", keycloak.token);
      Cookies.set("access_token", keycloak.token, { path: "/" });
      try {
        getTenantFromToken();
      } catch (e) {
        console.warn("⚠️ [KC] getTenantFromToken() failed:", e);
      }
    } else {
      console.warn("⚠️ [KC] Not authenticated after initKeycloak (check-sso).");
    }
  } catch (err) {
    // 🔴 Don't swallow this — let Navigator see it
    console.error("❌ [KC] initKeycloak() error:", err && err.message ? err.message : err);
    throw err;  // let Navigator's try/catch handle it
  } finally {
    // 🔧 Clean auth params (include iss to avoid URL spam)
    try {
      const url = new URL(window.location.href);
      let changed = false;
      ["code", "state", "session_state", "iss"].forEach((p) => {
        if (url.searchParams.has(p)) {
          console.log("🔧 [KC] Removing auth param from URL:", p);
          url.searchParams.delete(p);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState({}, "", url.toString());
      }
    } catch (e) {
      console.warn("⚠️ [KC] Failed to clean auth params from URL:", e);
    }
  }

  return keycloak;
};

// ---------- Auth utils ----------

// ✅ Check if User is Authenticated
export const isAuthenticated = () => {
  const keycloak = getKeycloakInstance();
  const auth = !!keycloak.authenticated;
  console.log("🔍 [KC] isAuthenticated() =>", auth, "(raw value:", keycloak.authenticated, ")");
  return auth;
};

/* 11/29 ACB Modified
// ✅ Get Access Token (Checks Cookies, localStorage, sessionStorage)
export const getToken = () => {
  const cookieToken = Cookies.get("access_token");
  const localToken = localStorage.getItem("access_token");
  const sessionToken = sessionStorage.getItem("access_token");

  console.log("🔍 [KC] getToken() values:", {
    cookieTokenPresent: !!cookieToken,
    localTokenPresent: !!localToken,
    sessionTokenPresent: !!sessionToken,
  });

  return cookieToken || localToken || sessionToken;
};*/

export const getToken = () => {
  const cookieToken = Cookies.get("access_token");
  const localToken = localStorage.getItem("access_token");
  const sessionToken = sessionStorage.getItem("access_token");

  // 🔹 NEW: also check the live Keycloak instance for a token
  const fromInstance =
    keycloakInstance && keycloakInstance.token ? keycloakInstance.token : null;

  console.log("🔍 [KC] getToken() values:", {
    cookieTokenPresent: !!cookieToken,
    localTokenPresent: !!localToken,
    sessionTokenPresent: !!sessionToken,
    keycloakInstanceTokenPresent: !!fromInstance,
  });

  return cookieToken || localToken || sessionToken || fromInstance || null;
};


// ✅ Start Token Refresh (Every 60 Seconds)
export const startTokenRefresh = () => {
  const keycloak = getKeycloakInstance();
  console.log("🔧 [KC] startTokenRefresh() called");

  setInterval(async () => {
    try {
      console.log("🔹 [KC] Calling keycloak.updateToken(30)...");
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        console.log("🔹 [KC] Token refreshed. New token present:", !!keycloak.token);
        localStorage.setItem("access_token", keycloak.token);
        sessionStorage.setItem("access_token", keycloak.token);
        Cookies.set("access_token", keycloak.token, { path: "/" });
      } else {
        console.warn("⚠️ [KC] Token still valid, no refresh needed.");
      }
    } catch (error) {
      console.error("❌ [KC] Failed to refresh token, logging out user. Error:", error);
      logout();
    }
  }, 60000); // ✅ Check every minute
};

// ---------- Admin token helper (unchanged, but logged) ----------

export const getAdminToken = async () => {
  console.log("🔧 [KC] getAdminToken() called");
  try {
    const response = await axios.post(
      "https://keycloak.idenza.ai/realms/master/protocol/openid-connect/token",
      new URLSearchParams({
        client_id: "admin-cli",
        username: "admin", // ✅ Replace with actual admin username
        password: "your-admin-password", // ✅ Replace securely
        grant_type: "password",
      }),
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );

    console.log("✅ [KC] Admin Token Retrieved (length):", response.data.access_token.length);
    return response.data.access_token;
  } catch (error) {
    console.error(
      "❌ [KC] Error fetching admin token:",
      error.response ? error.response.data : error.message
    );
    return null;
  }
};

// ---------- Store Token ----------

export const storeToken = (token) => {
  console.log("🔧 [KC] storeToken() called. Token present:", !!token);
  if (token) {
    localStorage.setItem("access_token", token);
    sessionStorage.setItem("access_token", token);
    Cookies.set("access_token", token, {
      path: "/", // let browser use current host as domain
    });
    console.log("✅ [KC] Token stored successfully in localStorage, sessionStorage, and cookie.");
  } else {
    console.error("❌ [KC] storeToken() called with no token.");
  }
};

// ---------- Tenant / username extraction ----------

export const getTenantFromToken = () => {
  const keycloak = getKeycloakInstance();

  if (!keycloak.tokenParsed) {
    console.warn("⚠️ [KC] getTenantFromToken(): No parsed token available.");
    return null;
  }

  console.log("🔹 [KC] Full Decoded Token (tokenParsed):", keycloak.tokenParsed);

  const tenantId = keycloak.tokenParsed.tenant_id; // comes from hardcoded claim per realm
  const username =
    keycloak.tokenParsed.preferred_username ||
    keycloak.tokenParsed.name ||
    keycloak.tokenParsed.sub;

  console.log("🔍 [KC] Extracted from token:", { tenantId, username });

  if (!tenantId) {
    console.error("❌ [KC] ERROR: Tenant ID not found in token!");
    return null;
  }

  sessionStorage.setItem("tenant_id", tenantId);
  sessionStorage.setItem("username", username);

  // ✅ NEW
  //sessionStorage.setItem("active_realm", tenantId);
  sessionStorage.setItem("active_realm", keycloak.realm);

  console.log("✅ [KC] Stored tenant_id and username in sessionStorage.");
  return { tenantId, username };
};

// ---------- Logout ----------

export const logout = () => {
  console.log("✅ [KC] logout() called. Clearing tokens and session info...");

  // ✅ capture current instance BEFORE clearing realm/storage
  const kc = keycloakInstance;

  localStorage.removeItem("access_token");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("tenant_id");
  sessionStorage.removeItem("username");
  sessionStorage.removeItem("active_realm");
  Cookies.remove("access_token", { path: "/" });

  // ✅ hard reset singleton so next init will rebuild using URL realm
  keycloakInstance = null;
  keycloakInitialized = false;

  const redirectUri = window.location.origin + "/login";
  console.log("🔧 [KC] Logging out. redirectUri:", redirectUri);

  try {
    if (kc && typeof kc.logout === "function") {
      kc.logout({ redirectUri });
      return;
    }
  } catch (e) {
    console.warn("⚠️ [KC] kc.logout failed, falling back to redirect:", e);
  }

  // fallback: just go to login page
  window.location.href = redirectUri;
};
